import { Box } from '@mui/material';
import { FinalGridContainer } from '@portals/core/src/components/FinalGrid/components/FinalGridContainer/FinalGridContainer';
import { FinalGridItem } from '@portals/core/src/components/FinalGrid/components/FinalGridItem/FinalGridItem';
import { Burger, HomeInverted } from '@portals/icons';
import i18next from 'i18next';
import { LatLngExpression } from 'leaflet';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { CircleMarker } from '../../components/CircleMarker/CircleMarker';
import { FitToBoundsHandler } from '../../components/FitToBoundsHandler/FitToBoundsHandler';
import { YellowMapTracker } from '../../components/YellowMapTracker/YellowMapTracker';
import { MapContainer, type MapContainerProps } from '../MapContainer/MapContainer';
import { MarkerClusterGroup } from '../MarkerClusterGroup/MarkerClusterGroup';
import { LocationsList } from './LocationsList';

// eslint-disable-next-line import/no-named-as-default-member
const fixedT = i18next.getFixedT.bind(i18next)(null, 'maps', 'text');

function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

const GERMANY: [[number, number], [number, number]] = [
  [44.355278211602, -7.91015625],
  [56.956957113368, 28.76220703125],
];

// eslint-disable-next-line @if6/fi-no-absolute-urls
const DEFAULT_TILE_URL = 'https://{s}.yellowmaps.eu/{t}/{z}/{x}/{y}{r}.png';

export interface Location {
  id: number;
  headline?: string;
  address?: string;
  coordinates?: {
    latitude?: number;
    longitude?: number;
  };
}

interface LocationsMapProps extends Omit<MapContainerProps, 'url'> {
  url?: string;
  resultList?: ReactElement;
  locations?: Array<Location>;
  sessionUrl?: string;
  frontendKey?: string;
  channel?: string;
  maxBounds?: [[number, number], [number, number]];
}

export const LocationsMap: React.FC<LocationsMapProps> = ({
  loading = false,
  url = DEFAULT_TILE_URL,
  resultList,
  locations = [],
  containerProps = {},
  sessionUrl,
  frontendKey,
  channel = '',
  maxBounds = GERMANY,
  ...props
}) => {
  const [active, setActive] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [activeMarkerCoordinates, setActiveMarkerCoordinates] = useState<LatLngExpression[] | null>(null);

  const coordinates = useMemo<LatLngExpression[]>(() => {
    if (activeMarkerCoordinates) {
      return activeMarkerCoordinates;
    }
    return locations.map((item) => [item.coordinates?.latitude, item.coordinates?.longitude]);
  }, [locations, activeMarkerCoordinates]);

  useEffect(() => {
    if (!locations.length) {
      setActive(null);
      setActiveMarkerCoordinates(null);
      setHover(null);
      return;
    }

    if (!locations.find(({ id }) => id === active)) {
      setActive(null);
      setActiveMarkerCoordinates(null);
    }

    if (!locations.find(({ id }) => id === hover)) {
      setHover(null);
    }
  }, [active, locations, hover]);

  const markerClick = useCallback((event) => {
    setActive(event.id);
    setActiveMarkerCoordinates([event.latlng]);
  }, []);

  const markerKeyDown = useCallback((event) => {
    if (event.originalEvent.key === 'Enter') {
      setActive(event.id);
      setActiveMarkerCoordinates([event.latlng]);
    }
  }, []);

  const listItemClick = useCallback((location: Location) => {
    setActive(location.id);
    setActiveMarkerCoordinates([[location.coordinates?.latitude, location.coordinates?.longitude]]);
  }, []);

  const markerMouseOver = useCallback((event) => {
    if (isTouchDevice()) {
      return;
    }
    setHover(event.id);
  }, []);

  const markerMouseOut = useCallback(() => {
    if (isTouchDevice()) {
      return;
    }
    setHover(null);
  }, []);

  const clearActive = useCallback((event) => {
    setActive(null);
    setActiveMarkerCoordinates(null);
    setHover(null);

    event.preventDefault();
    event.stopPropagation();

    return false;
  }, []);

  return (
    <FinalGridContainer>
      <FinalGridItem lg={6} md={6} sm={12} xs={12} order={{ xs: 2, sm: 2, md: 1, lg: 1 }}>
        {resultList ? (
          resultList
        ) : (
          <LocationsList
            locations={locations}
            activeId={active}
            clearActive={clearActive}
            hoverId={hover}
            loading={loading}
            onListItemClick={listItemClick}
            t={fixedT}
          />
        )}
      </FinalGridItem>
      <FinalGridItem lg={6} md={6} sm={12} xs={12} order={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
        <Box sx={{ position: 'sticky', top: 0 }}>
          <MapContainer
            style={{ width: '100%', height: '100%' }}
            containerProps={containerProps}
            maxBounds={maxBounds}
            loading={loading}
            url={url}
            {...props}
          >
            {locations?.length > 0 && (
              <>
                <FitToBoundsHandler coordinates={coordinates} padding={12} fly={false} />
                {activeMarkerCoordinates && (
                  <FitToBoundsHandler coordinates={activeMarkerCoordinates} padding={12} fly={false} />
                )}
                <MarkerClusterGroup
                  icon={<Burger fontSize="inherit" />}
                  disableClusteringAtZoom={18}
                  spiderfyOnMaxZoom={false}
                >
                  {locations.map((item, index) => (
                    <CircleMarker
                      id={item.id}
                      key={item.id}
                      position={[item.coordinates?.latitude, item.coordinates?.longitude]}
                      onClick={markerClick}
                      onKeyDown={markerKeyDown}
                      onMouseOver={markerMouseOver}
                      onMouseOut={markerMouseOut}
                      title={fixedT('markerclick', { label: item.headline })}
                    >
                      <>
                        {hover == item.id ? index + 1 : null}
                        {hover != item.id ? <HomeInverted fontSize="inherit" /> : null}
                      </>
                    </CircleMarker>
                  ))}
                </MarkerClusterGroup>
              </>
            )}
            {sessionUrl && frontendKey && (
              <YellowMapTracker
                trackingEndpoint={`${sessionUrl}&channel=${channel}&apiKey=${encodeURIComponent(frontendKey)}`}
              />
            )}
          </MapContainer>
        </Box>
      </FinalGridItem>
    </FinalGridContainer>
  );
};
